import { Product } from '../types/content'

// Branded Republic packaging: navy label, drug name and strength in brick serif,
// ring mark and letterspaced REPUBLIC wordmark — per the Direction 1 design file.
const IMAGE = {
  tirzepatide:
    'https://cdn.magicpatterns.com/patterns/generated-images/788cc45f-10d6-4e88-b016-ba6af3b006f3.jpg',
  semaglutide:
    'https://cdn.magicpatterns.com/patterns/generated-images/d6b940ce-2ecc-4638-801f-4b50cba80d12.jpg',
  testosterone:
    'https://cdn.magicpatterns.com/patterns/generated-images/f370d57a-195f-4e5b-b545-501ce7036a8d.jpg',
  estradiol:
    'https://cdn.magicpatterns.com/patterns/generated-images/e912440c-ee6d-41b1-af90-90bd2ddac523.jpg',
  tadalafil:
    'https://cdn.magicpatterns.com/patterns/generated-images/0843d800-553b-4123-88ab-572023cb245a.jpg',
  topical:
    'https://cdn.magicpatterns.com/patterns/generated-images/8b8293f5-7fa9-4b3a-ab23-e08e79e79c0b.jpg',
  tesamorelin:
    'https://cdn.magicpatterns.com/patterns/generated-images/8450246b-3b81-4ced-976d-70721297b7c8.jpg',
  bpc157:
    'https://cdn.magicpatterns.com/patterns/generated-images/7ef8ff2a-fe98-4893-91ae-0752ab62fb68.jpg',
  nad: 'https://cdn.magicpatterns.com/patterns/generated-images/36ddd943-4573-4666-80db-b8c045bd062f.jpg',
  enclomiphene:
    'https://cdn.magicpatterns.com/patterns/generated-images/1b1162ba-292b-41a6-be61-d6be1b13debe.jpg',
}

export const products: Product[] = [
  {
    slug: 'tirzepatide',
    name: 'Tirzepatide',
    category: 'FDA-approved',
    status: 'FDA-approved · brand',
    form: 'Single-dose injector pen',
    strength: '2.5mg – 15mg weekly',
    price: 449,
    cadence: 'per month',
    priceNote: '4 weekly pens · dose changes at no extra cost',
    image: IMAGE.tirzepatide,
    tagline: 'Dual GIP/GLP-1 therapy for weight and metabolic health.',
    description:
      'The branded, FDA-approved dual GIP/GLP-1 receptor agonist. Republic dispenses it at the published pharmacy rate plus a fixed handling fee, with the escalation schedule written by your clinician rather than applied automatically.',
    pharmacy: 'US retail wholesaler',
    program: 'weight-management',
    rxRequired: true,
    highlights: [
      'Average 18–22% total body weight reduction at 72 weeks in trial data',
      'Weekly self-administered pen, no reconstitution',
      'Cold-chain shipping included',
      'Titration reviewed at every dose step',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous injection, weekly' },
      { label: 'Storage', value: 'Refrigerated 2–8°C, 21 days at room temp' },
      { label: 'Ships in', value: '48 hours of protocol approval' },
      { label: 'Requires', value: 'Baseline metabolic panel' },
    ],
  },
  {
    slug: 'semaglutide',
    name: 'Semaglutide',
    category: 'FDA-approved',
    status: 'FDA-approved · brand',
    form: 'Single-dose injector pen',
    strength: '0.25mg – 2.4mg weekly',
    price: 399,
    cadence: 'per month',
    priceNote: '4 weekly pens · price held 12 months',
    image: IMAGE.semaglutide,
    tagline: 'The most studied GLP-1 for sustained weight reduction.',
    description:
      'Branded FDA-approved semaglutide for chronic weight management. Your clinician sets the escalation pace against tolerability and lab work, and the monthly figure you see here is the monthly figure you are charged.',
    pharmacy: 'US retail wholesaler',
    program: 'weight-management',
    rxRequired: true,
    highlights: [
      'Extensive cardiovascular outcome data',
      'Weekly pen with fixed-dose delivery',
      'Nutrition and resistance-training guidance included',
      'Side-effect support within one business day',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous injection, weekly' },
      { label: 'Storage', value: 'Refrigerated 2–8°C' },
      { label: 'Ships in', value: '48 hours of protocol approval' },
      { label: 'Requires', value: 'Baseline metabolic panel' },
    ],
  },
  {
    slug: 'testosterone-cypionate',
    name: 'Testosterone Cypionate',
    category: 'FDA-approved',
    status: 'FDA-approved · generic',
    form: '10mL multi-dose vial',
    strength: '200mg/mL',
    price: 89,
    cadence: 'per month',
    priceNote: 'Includes syringes, needles and sharps return',
    image: IMAGE.testosterone,
    tagline: 'The clinical standard for lab-confirmed low testosterone.',
    description:
      'FDA-approved generic testosterone cypionate, dispensed by a US-licensed pharmacy named on your documentation. Supplies are included — Republic does not charge separately for what you need to take the medication.',
    pharmacy: 'US-licensed retail pharmacy',
    program: 'hormone-optimization-men',
    rxRequired: true,
    highlights: [
      'Weekly or twice-weekly protocols',
      'All injection supplies included',
      'Estradiol monitored at week 8 and month 6',
      'Fertility-preserving alternatives available',
    ],
    facts: [
      { label: 'Route', value: 'Intramuscular or subcutaneous' },
      { label: 'Storage', value: 'Room temperature, protect from light' },
      { label: 'Schedule', value: 'Controlled substance, Schedule III' },
      { label: 'Requires', value: 'Baseline hormone panel' },
    ],
  },
  {
    slug: 'estradiol-progesterone',
    name: 'Estradiol + Progesterone',
    category: 'FDA-approved',
    status: 'FDA-approved · generic',
    form: 'Transdermal pump and oral capsule',
    strength: '0.05mg/day + 100mg nightly',
    price: 64,
    cadence: 'per month',
    priceNote: 'Both medications, one price',
    image: IMAGE.estradiol,
    tagline: 'Menopausal hormone therapy at a rate you can plan around.',
    description:
      'Body-identical estradiol delivered transdermally with micronised progesterone taken nightly — the pairing most guidelines favour for women with an intact uterus. Dose adjustments are included in your membership.',
    pharmacy: 'US-licensed retail pharmacy',
    program: 'hormone-therapy-women',
    rxRequired: true,
    highlights: [
      'Transdermal route avoids first-pass metabolism',
      'Cyclic or continuous progesterone regimens',
      'Low-dose testosterone available where indicated',
      'Quarterly protocol review included',
    ],
    facts: [
      { label: 'Route', value: 'Transdermal daily, oral nightly' },
      { label: 'Storage', value: 'Room temperature' },
      { label: 'Ships in', value: '48 hours of protocol approval' },
      { label: 'Requires', value: 'Symptom assessment and panel' },
    ],
  },
  {
    slug: 'tadalafil',
    name: 'Tadalafil',
    category: 'FDA-approved',
    status: 'FDA-approved · generic',
    form: 'Oral tablet',
    strength: '5mg daily or 20mg as needed',
    price: 39,
    cadence: 'per month',
    priceNote: '30 tablets · plain outer packaging',
    image: IMAGE.tadalafil,
    tagline: 'Daily or as-needed, in unbranded packaging.',
    description:
      'FDA-approved generic tadalafil, prescribed after a clinician reviews your cardiovascular history and the hormonal picture underneath the symptom rather than defaulting to a script.',
    pharmacy: 'US-licensed retail pharmacy',
    program: 'sexual-health',
    rxRequired: true,
    highlights: [
      'Daily low-dose or on-demand dosing',
      'Cardiovascular screening included in intake',
      'Formulation changes at no extra cost',
      'Shipped in plain, unbranded packaging',
    ],
    facts: [
      { label: 'Route', value: 'Oral' },
      { label: 'Storage', value: 'Room temperature' },
      { label: 'Ships in', value: '24 hours of clinician review' },
      { label: 'Requires', value: 'Focused intake' },
    ],
  },
  {
    slug: 'finasteride-minoxidil',
    name: 'Finasteride + Minoxidil',
    category: 'Compounded',
    status: 'Compounded · 503A pharmacy',
    form: 'Topical solution',
    strength: '0.1% / 6%',
    price: 32,
    cadence: 'per month',
    priceNote: '60mL bottle · 3-month supply option available',
    image: IMAGE.topical,
    tagline: 'One topical instead of two routines.',
    description:
      'A compounded topical that combines both first-line agents for androgenic hair loss in a single nightly application, prepared by a US-licensed 503A pharmacy against your prescription.',
    pharmacy: 'US-licensed 503A pharmacy',
    program: 'hair-and-skin',
    rxRequired: true,
    highlights: [
      'Topical route reduces systemic exposure',
      'Photo-based progress review at month 3 and 6',
      'Oral low-dose minoxidil available as an alternative',
      'Three-month supply shipping reduces cost per month',
    ],
    facts: [
      { label: 'Route', value: 'Topical, nightly' },
      { label: 'Storage', value: 'Room temperature' },
      { label: 'Ships in', value: '3–5 days of approval' },
      { label: 'Requires', value: 'Photo assessment' },
    ],
  },
  {
    slug: 'tesamorelin',
    name: 'Tesamorelin',
    category: 'Peptide',
    status: 'Compounded · 503A pharmacy',
    form: 'Lyophilised vial with diluent',
    strength: '10mg',
    price: 219,
    cadence: 'per month',
    priceNote: 'Includes bacteriostatic water and syringes',
    image: IMAGE.tesamorelin,
    tagline: 'Growth-hormone-releasing factor analogue, cycled deliberately.',
    description:
      'A GHRH analogue used in Republic longevity protocols for visceral fat and body composition. Dispensed with a written cycling and washout schedule and a follow-up panel before any renewal.',
    pharmacy: 'US-licensed 503A pharmacy',
    program: 'longevity-peptides',
    rxRequired: true,
    highlights: [
      'Nightly subcutaneous dosing, cycled 12 weeks on',
      'IGF-1 monitored before and during the cycle',
      'Lot documentation supplied with every shipment',
      'Reconstitution supplies included',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, nightly' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Ships in', value: '3–5 days of approval' },
      { label: 'Requires', value: 'Extended longevity panel' },
    ],
  },
  {
    slug: 'bpc-157',
    name: 'BPC-157',
    category: 'Peptide',
    status: 'Compounded · 503A pharmacy',
    form: 'Lyophilised vial with diluent',
    strength: '5mg',
    price: 149,
    cadence: 'per month',
    priceNote: 'One vial · typical 8-week recovery cycle',
    image: IMAGE.bpc157,
    tagline: 'Recovery protocols with documented provenance.',
    description:
      'A compounded pentadecapeptide used in soft-tissue recovery protocols. Republic dispenses only US-compounded material with lot documentation — never overseas-sourced vials sold as research chemicals.',
    pharmacy: 'US-licensed 503A pharmacy',
    program: 'longevity-peptides',
    rxRequired: true,
    highlights: [
      'Injectable or oral capsule formulations',
      'Defined 8-week cycle with washout',
      'Lot and beyond-use date on every vial',
      'Clinician review before renewal',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous or oral' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Ships in', value: '3–5 days of approval' },
      { label: 'Requires', value: 'Clinician review' },
    ],
  },
  {
    slug: 'nad-plus',
    name: 'NAD+',
    category: 'Peptide',
    status: 'Compounded · 503B pharmacy',
    form: 'Multi-dose vial',
    strength: '100mg/mL',
    price: 129,
    cadence: 'per month',
    priceNote: 'Weekly dosing · supplies included',
    image: IMAGE.nad,
    tagline: 'Subcutaneous NAD+ without the infusion-clinic markup.',
    description:
      'Compounded NAD+ for weekly subcutaneous dosing, prepared by a US-licensed 503B outsourcing facility. Priced at a fraction of an infusion-clinic visit for the same monthly quantity.',
    pharmacy: 'US-licensed 503B facility',
    program: 'longevity-peptides',
    rxRequired: true,
    highlights: [
      'Weekly at-home subcutaneous dosing',
      'No clinic visit or infusion fee',
      'Titration guidance for injection-site tolerance',
      'Supplies and sharps return included',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, weekly' },
      { label: 'Storage', value: 'Refrigerated 2–8°C' },
      { label: 'Ships in', value: '3–5 days of approval' },
      { label: 'Requires', value: 'Clinician review' },
    ],
  },
  {
    slug: 'enclomiphene',
    name: 'Enclomiphene',
    category: 'Compounded',
    status: 'Compounded · 503A pharmacy',
    form: 'Oral capsule',
    strength: '12.5mg – 25mg',
    price: 79,
    cadence: 'per month',
    priceNote: '30 capsules · fertility-preserving option',
    image: IMAGE.enclomiphene,
    tagline: 'Raise your own testosterone rather than replace it.',
    description:
      'A compounded selective estrogen receptor modulator used where preserving fertility and endogenous production matters. Suitable for a specific subset of patients — your clinician will tell you plainly if you are not one of them.',
    pharmacy: 'US-licensed 503A pharmacy',
    program: 'hormone-optimization-men',
    rxRequired: true,
    highlights: [
      'Preserves LH, FSH and fertility',
      'Oral, no injections required',
      'Week-8 panel confirms response',
      'Switch to injectable therapy at any time',
    ],
    facts: [
      { label: 'Route', value: 'Oral, daily or alternate days' },
      { label: 'Storage', value: 'Room temperature' },
      { label: 'Ships in', value: '3–5 days of approval' },
      { label: 'Requires', value: 'Baseline hormone panel' },
    ],
  },
]

export function getProduct(slug: string): Product | undefined {
  return products.find((product) => product.slug === slug)
}
